<template>
  <q-card v-if="person">
    <q-toolbar class="bg-primary text-white">
      <q-btn
        icon="fa fa-close"
        flat
        round
        dense
        @click="$emit('close')"
      />
      <q-toolbar-title>
        {{ person.label || 'Unnamed Person' }}
      </q-toolbar-title>
      <q-btn
        v-if="isGranted('MANAGE_PERSONS')"
        icon="fa fa-edit"
        flat
        round
        dense
        @click="editPerson"
      >
        <q-tooltip>Edit label</q-tooltip>
      </q-btn>
      <span class="text-caption q-ml-sm">
        {{ total }}
        face{{ total !== 1 ? 's' : '' }}
      </span>
    </q-toolbar>

    <div class="detail-body">
      <div class="detail-body__posts">
        <q-card-section>
          <div class="text-h6 q-mb-md">Tagged Posts
          </div>
          <q-linear-progress
            v-if="loading"
            indeterminate
            color="primary"
            class="q-mb-md"
          />

          <div
            v-if="tags.length"
            class="row q-col-gutter-md"
          >
            <div
              v-for="tag in tags"
              :key="tag.id"
              class="col-xs-12 col-sm-6 col-md-4"
            >
              <PostTagCard :tag="tag" />
            </div>
          </div>

          <div
            v-if="hasMore"
            class="text-center q-mt-md"
          >
            <q-btn
              color="primary"
              label="Load More"
              :loading="loadingMore"
              flat
              dense
              @click="loadMore"
            />
          </div>

          <div
            v-if="!loading && tags.length === 0"
            class="text-center text-grey-6 q-pa-xl"
          >
            No tagged posts found for this person
          </div>
        </q-card-section>
      </div>

      <div class="detail-body__faces">
        <PersonFacesGrid
          ref="facesGrid"
          :client-id="clientId"
          :person-id="person.id"
        />
      </div>
    </div>

    <EditPersonModal
      ref="editPersonModal"
      @updated="onPersonUpdated"
    />
  </q-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Person } from '@/analysis/types';
import { PostTag } from '@/type';
import isGranted from '@/auth/isGranted';
import postTagRepository from '@/social/post-tag/post-tag-repository';
import PostTagCard from './PostTagCard.vue';
import EditPersonModal from './EditPersonModal.vue';
import PersonFacesGrid from './PersonFacesGrid.vue';

interface Data {
  person: Person | null;
  tags: PostTag[];
  loading: boolean;
  loadingMore: boolean;
  total: number;
  offset: number;
}

const PAGE_SIZE = 20;

export default defineComponent({
  emits: ['close', 'updated'],
  components: {
    PostTagCard,
    EditPersonModal,
    PersonFacesGrid,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      person: null,
      tags: [],
      loading: false,
      loadingMore: false,
      total: 0,
      offset: 0,
    };
  },
  computed: {
    hasMore(): boolean {
      return this.tags.length < this.total;
    },
  },
  methods: {
    isGranted,
    async open(person: Person) {
      this.person = person;
      this.tags = [];
      this.total = 0;
      this.loading = true;

      try {
        const result = await postTagRepository.findAll(
          this.clientId,
          {
            resources: { person: [person.id] },
            limit: PAGE_SIZE,
            offset: 0,
          },
        );
        this.tags = result.items;
        this.total = result.total;
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (!this.person) return;
      this.loadingMore = true;

      try {
        const result = await postTagRepository.findAll(
          this.clientId,
          {
            resources: { person: [this.person.id] },
            limit: PAGE_SIZE,
            offset: this.tags.length,
          },
        );
        this.tags.push(...result.items);
      } finally {
        this.loadingMore = false;
      }
    },

    editPerson() {
      if (this.person) {
        (this.$refs.editPersonModal as any).open(this.person);
      }
    },

    onPersonUpdated(person: Person) {
      this.person = person;
      this.$emit('updated', person);
    },
  },
});
</script>

<style scoped>
.detail-body {
  display: flex;
  flex-direction: row;
  min-height: 0;
  flex: 1;
}

.detail-body__posts {
  flex: 1;
  overflow-y: auto;
}

.detail-body__faces {
  width: 200px;
  flex-shrink: 0;
  border-left: 1px solid #e0e0e0;
  overflow-y: auto;
}
</style>
